import React, { useCallback, useEffect, useRef, useState } from "react";
import ActionForm from "./action_form";
import ClientSearch from "./client_search";
import History from "./history";
import Item from "./item";
import Modal from "./modal";
import { request, requestKey, time } from "./api";

export default function Board() {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [stale, setStale] = useState(true);
  const [lastSuccess, setLastSuccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState("all");
  const [encounterId, setEncounterId] = useState(null);
  const [action, setAction] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [historySearch, setHistorySearch] = useState(false);
  const sequence = useRef(0);
  const inFlight = useRef(false);
  const writing = useRef(false);
  const mounted = useRef(true);
  const seenRetry = useRef(null);
  const lastSuccessRef = useRef(0);

  const refresh = useCallback(async (force = false) => {
    if (inFlight.current && !force) return;
    const version = ++sequence.current;
    inFlight.current = true;
    try {
      const data = await request("snapshot");
      if (!mounted.current || sequence.current !== version) return;
      setBoard(data);
      setStale(false);
      setError("");
      lastSuccessRef.current = Date.now();
      setLastSuccess(new Date());
    } catch (e) {
      if (!mounted.current || sequence.current !== version) return;
      setStale(true);
      setError(e.message);
      if (e.sessionExpired) setExpired(true);
    } finally {
      if (version === sequence.current) inFlight.current = false;
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    refresh();
    // Keep polling while a second-monitor window is unfocused.
    const timer = setInterval(() => {
      if (Date.now() - lastSuccessRef.current > 20000) setStale(true);
      refresh();
    }, 5000);
    const focused = () => refresh(true);
    const offline = () => {
      setStale(true);
      setError("Connection lost. Reconnect before recording care.");
    };
    window.addEventListener("focus", focused);
    window.addEventListener("online", focused);
    window.addEventListener("offline", offline);
    return () => {
      mounted.current = false;
      sequence.current++;
      clearInterval(timer);
      window.removeEventListener("focus", focused);
      window.removeEventListener("online", focused);
      window.removeEventListener("offline", offline);
    };
  }, [refresh]);
  const disabled = busy || stale || expired;
  const mutate = async (payload) => {
    if (writing.current) throw new Error("Please wait for the current action to finish.");
    writing.current = true;
    setBusy(true);
    sequence.current++;
    try {
      const result = await request("command", payload);
      await refresh(true);
      setNotice(result.message || "Saved.");
      return result;
    } catch (e) {
      if (e.sessionExpired) {
        setExpired(true);
        setStale(true);
      } else if (!e.status) {
        setStale(true);
        setError(e.message);
      }
      // Refresh after validation/conflict too, without losing the user's open form.
      if (e.status) await refresh(true);
      throw e;
    } finally {
      writing.current = false;
      setBusy(false);
    }
  };
  const logSeen = async (client) => {
    if (disabled || writing.current) return;
    if (seenRetry.current?.clientId !== client.id)
      seenRetry.current = { clientId: client.id, key: requestKey() };
    try {
      const result = await mutate({
        operation: "sighting",
        client_id: client.id,
        request_key: seenRetry.current.key
      });
      seenRetry.current = null;
      setEncounterId(result.sighting_id);
      setNotice(`${client.name} logged as seen.`);
    } catch (e) {
      setError(e.message);
    }
  };
  const openAction = (next) => {
    document
      .querySelectorAll(".ted-item-menu[open]")
      .forEach((menu) => menu.removeAttribute("open"));
    setHistoryId(null);
    setAction(next);
  };
  const itemsFor = (id) =>
    board.items
      .filter((item) => item.client_id === id)
      .sort(
        (a, b) =>
          (a.kind === "alert" ? 0 : 1) - (b.kind === "alert" ? 0 : 1) ||
          new Date(a.created_at) - new Date(b.created_at)
      );
  const latestSighting = (id) =>
    board.sightings.filter((sighting) => sighting.client_id === id).slice(-1)[0];
  const latestEncounter = (id) => latestSighting(id)?.id;
  const oldestCare = (id) =>
    Math.min(...itemsFor(id).map((item) => new Date(item.created_at).getTime()));
  const itemView = (item, client, sightingId) => (
    <Item
      key={item.id}
      item={item}
      client={client}
      board={board}
      encounterId={sightingId}
      disabled={disabled}
      onAction={openAction}
    />
  );
  const clientName = (client) => (
    <button type="button" className="cb-name" onClick={() => setHistoryId(client.id)}>
      {client.name}
    </button>
  );
  if (!board)
    return (
      <div className="ted-care-board">
        <div className="cb-container cb-content">
          <h1>TED Care Board</h1>
          <p role={error ? "alert" : "status"}>{error || "Loading board…"}</p>
          <button className="ccw-button" onClick={() => refresh(true)}>
            Retry
          </button>
        </div>
      </div>
    );
  const activeClients = board.clients.filter(
    (client) => client.follow_up === "active" && itemsFor(client.id).length
  );
  const activeItems = board.items.filter((item) =>
    activeClients.some((client) => client.id === item.client_id)
  );
  const due = activeItems.filter((item) => item.review_due);
  const archived = board.clients.filter((client) => client.follow_up === "archived");
  const visibleClients = (filter === "archived" ? archived : activeClients)
    .filter(
      (client) =>
        filter === "all" ||
        filter === "archived" ||
        itemsFor(client.id).some((item) =>
          filter === "review" ? item.review_due : item.kind === filter
        )
    )
    .sort(
      (a, b) =>
        Number(itemsFor(b.id).some((item) => item.kind === "alert")) -
          Number(itemsFor(a.id).some((item) => item.kind === "alert")) ||
        oldestCare(a.id) - oldestCare(b.id)
    );
  const seenClients = board.clients
    .filter((client) => board.sightings.some((sighting) => sighting.client_id === client.id))
    .sort(
      (a, b) =>
        Number(b.follow_up === "active" && itemsFor(b.id).length > 0) -
          Number(a.follow_up === "active" && itemsFor(a.id).length > 0) ||
        new Date(latestSighting(b.id).seen_at) - new Date(latestSighting(a.id).seen_at)
    );
  const encounter = board.sightings.find((sighting) => sighting.id === encounterId);
  const encounterClient =
    encounter && board.clients.find((client) => client.id === encounter.client_id);
  return (
    <div className="ted-care-board">
      <div className="ccw-ui ccw-page">
        <section className="ccw-page-header">
          <div className="cb-container ccw-page-header__row">
            <div>
              <p className="ccw-page-header__eyebrow">TED · Community follow-up</p>
              <h1 className="ccw-page-header__title">Care Board</h1>
            </div>
            <div className="cb-shift">
              <div className="cb-shift__text">
                <strong>
                  {board.shift.date} · {board.shift.label}
                </strong>
                <span>
                  {time(board.shift.starts_at, board.time_zone)} –{" "}
                  {time(board.shift.ends_at, board.time_zone)} · {board.time_zone}
                </span>
                <span role="status">
                  {stale
                    ? "Updates unavailable"
                    : `Updated ${lastSuccess ? time(lastSuccess, board.time_zone) : "now"}`}
                </span>
              </div>
              <a className="cb-header-button" href="/ted_care_board" target="_blank" rel="noopener">
                Open in new window ↗
              </a>
            </div>
          </div>
        </section>
        <div className="cb-container cb-content">
          {(stale || error || expired) && (
            <div className="cb-banner cb-banner--warning" role="alert">
              <p>{error || "Updates are unavailable. Refresh before recording care."}</p>
              {expired ? (
                <a href="/users/sign_in" target="_blank" rel="noopener">
                  Sign in
                </a>
              ) : (
                <button className="ccw-button" onClick={() => refresh(true)}>
                  Reconnect
                </button>
              )}
              {expired && (
                <button className="ccw-button" onClick={() => window.location.reload()}>
                  Reload board
                </button>
              )}
            </div>
          )}
          {board.supervisor && due.length > 0 && (
            <div className="cb-banner cb-banner--warning">
              <p>
                <strong>
                  {due.length} persistent alert{due.length === 1 ? " needs" : "s need"} monthly
                  review.
                </strong>{" "}
                Keep, edit or close each alert.
              </p>
              <button className="ccw-button" onClick={() => setFilter("review")}>
                Review alerts
              </button>
            </div>
          )}
          {notice && (
            <div className="cb-banner cb-banner--info" role="status">
              <p>{notice}</p>
              <button
                type="button"
                className="cb-icon-button"
                aria-label="Dismiss message"
                onClick={() => setNotice("")}
              >
                ×
              </button>
            </div>
          )}
          <section className="ccw-panel cb-entry">
            <div className="cb-entry__title">
              <h2>Log a client as seen</h2>
              <p className="cb-muted">Choose a result to record a sighting at TED.</p>
              <button type="button" className="cb-link" onClick={() => setHistorySearch(true)}>
                Find care history
              </button>
            </div>
            <ClientSearch onSelect={logSeen} disabled={disabled} />
          </section>
          {encounterClient && (
            <section className="cb-encounter">
              <div className="cb-section-head">
                <h2>
                  {encounterClient.name} · seen at {time(encounter.seen_at, board.time_zone)}
                </h2>
                <button
                  className="ccw-button ccw-button--quiet"
                  onClick={() => setEncounterId(null)}
                >
                  Close panel
                </button>
              </div>
              {encounterClient.follow_up === "archived" && (
                <p>
                  Archived follow-up. Preserved notices are shown below. A supervisor must
                  reactivate follow-up before recording care actions.
                </p>
              )}
              {itemsFor(encounterClient.id).length ? (
                itemsFor(encounterClient.id).map((item) =>
                  itemView(item, encounterClient, encounter.id)
                )
              ) : (
                <p>Nothing pending.</p>
              )}
              {clientName(encounterClient)}
            </section>
          )}
          <section className="cb-metrics" aria-label="Board summary">
            {[
              [activeItems.length, "Open items"],
              [activeItems.filter((item) => item.kind === "alert").length, "Alerts"],
              [activeClients.length, "Clients with care"],
              [seenClients.length, "Seen this shift"]
            ].map(([value, label]) => (
              <div className="cb-metric" key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </section>
          <div className="cb-grid">
            <section className="cb-care">
              <div className="cb-section-head">
                <div>
                  <h2>Outstanding Care</h2>
                  <p className="cb-muted">
                    Open care stays here, whether or not the client was seen today.
                  </p>
                </div>
                {board.supervisor && (
                  <button
                    className="ccw-button ccw-button--primary"
                    disabled={disabled}
                    onClick={() => openAction({ operation: "create" })}
                  >
                    Add task or alert
                  </button>
                )}
              </div>
              <div className="ted-filters" role="group" aria-label="Filter care">
                {[
                  ["all", `All ${activeItems.length}`],
                  ["alert", "Alerts"],
                  ["task", "Tasks"],
                  ...(board.supervisor ? [["review", `Review due ${due.length}`]] : []),
                  ["archived", `Archived ${archived.length}`]
                ].map(([value, label]) => (
                  <button
                    className={`ccw-button ccw-button--compact ${
                      filter === value ? "ccw-button--primary" : ""
                    }`}
                    key={value}
                    aria-pressed={filter === value}
                    onClick={() => setFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="cb-care-grid">
                {visibleClients.map((client) => (
                  <article className="cb-card" aria-label={client.name} key={client.id}>
                    <div className="cb-card__header">
                      <div>
                        {clientName(client)}
                        <p className="cb-muted">
                          ID {client.id} · {client.residence}
                        </p>
                      </div>
                      <span className="ccw-badge">
                        {client.follow_up === "archived"
                          ? "Archived"
                          : `${itemsFor(client.id).length} open`}
                      </span>
                    </div>
                    {(client.deceased || client.deleted || client.inactive) && (
                      <p className="cb-muted">
                        {client.deleted
                          ? "Deleted profile"
                          : client.deceased
                          ? "Deceased"
                          : "Inactive profile"}{" "}
                        · TED history retained
                      </p>
                    )}
                    {itemsFor(client.id)
                      .filter(
                        (item) =>
                          ["all", "archived"].includes(filter) ||
                          (filter === "review" ? item.review_due : item.kind === filter)
                      )
                      .map((item) => itemView(item, client, latestEncounter(client.id)))}
                    {!itemsFor(client.id).length && <p>No preserved open items.</p>}
                    <div className="cb-card__footer">
                      <button className="cb-link" onClick={() => setHistoryId(client.id)}>
                        Details and history
                      </button>
                    </div>
                  </article>
                ))}
              </div>
              {!visibleClients.length && (
                <div className="ccw-panel cb-empty">
                  <h3>No care items in this view</h3>
                  <p>Choose another filter or add care for a client.</p>
                </div>
              )}
            </section>
            <aside className="ccw-panel cb-seen">
              <div className="cb-section-head">
                <h2>Seen This Shift</h2>
                <span className="ccw-badge">{seenClients.length}</span>
              </div>
              <p className="cb-muted">Clients with outstanding care appear first.</p>
              {!seenClients.length && <p>No sightings this shift.</p>}
              {seenClients.map((client) => {
                const sightings = board.sightings.filter(
                  (sighting) => sighting.client_id === client.id
                );
                return (
                  <div
                    className={`ted-seen-row ${
                      client.follow_up === "active" && itemsFor(client.id).length ? "" : "is-quiet"
                    }`}
                    key={client.id}
                  >
                    {clientName(client)}
                    <p>
                      {time(sightings[sightings.length - 1].seen_at, board.time_zone)} ·{" "}
                      {sightings.length} sighting{sightings.length === 1 ? "" : "s"}
                    </p>
                    <p className="cb-muted">
                      {client.follow_up === "archived"
                        ? "Archived follow-up"
                        : `${itemsFor(client.id).length} open items`}
                    </p>
                    <button className="cb-link" onClick={() => setHistoryId(client.id)}>
                      View sightings
                    </button>
                  </div>
                );
              })}
            </aside>
          </div>
        </div>
        {historySearch && (
          <Modal title="Find care history" onClose={() => setHistorySearch(false)}>
            <div className="cb-dialog__body ted-form-fields ted-history-search">
              <p>
                Search clients with TED care or sightings. Opening history does not record a
                sighting.
              </p>
              <ClientSearch
                purpose="history"
                onSelect={(client) => {
                  setHistorySearch(false);
                  setHistoryId(client.id);
                }}
              />
            </div>
            <div className="cb-dialog__footer">
              <button type="button" className="ccw-button" onClick={() => setHistorySearch(false)}>
                Cancel
              </button>
            </div>
          </Modal>
        )}
        {action && (
          <ActionForm
            action={action}
            board={board}
            disabled={stale || expired}
            onClose={() => setAction(null)}
            onSave={async (payload) => {
              await mutate(payload);
              setAction(null);
            }}
          />
        )}
        {historyId && !action && (
          <History
            clientId={historyId}
            board={board}
            disabled={disabled}
            onClose={() => setHistoryId(null)}
            onAction={openAction}
          />
        )}
      </div>
    </div>
  );
}
